import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Button,
  Card,
  Modal,
  message,
  Tooltip,
  Popconfirm,
  Tag,
  Input,
  Select,
  Icon
} from 'antd'
import {
  getAiVideos,
  deleteAiVideo,
  getAiVideoById
} from '../../services/api/aiVideoGeneration' // Adjust import path as needed
import './VideoList.scss'
import NewHeader from '../../components/NewHeader'
import ReactPlayer from 'react-player'
import AIVideoModal from './components/VideoGeneration/components/FinalVideo'

const { Search } = Input
const { Option } = Select

const VideoList = ({ onCreateNew, onEditVideo }) => {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [searchText, setSearchText] = useState('')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [previewModalVisible, setPreviewModalVisible] = useState(false)

  useEffect(() => {
    fetchVideos()
  }, [pagination.current, pagination.pageSize])

  const fetchVideos = async () => {
    setLoading(true)
    try {
      const offset = (pagination.current - 1) * pagination.pageSize
      const response = await getAiVideos(offset, pagination.pageSize)

      if (response.data) {
        setVideos(response.data.videos || response.data || [])
        setPagination(prev => ({
          ...prev,
          total: response.data.total || response.data.length || 0
        }))
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error)
      message.error('Failed to load videos')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async videoId => {
    try {
      await deleteAiVideo(videoId)
      message.success('Video deleted successfully')
      fetchVideos() // Refresh the list
    } catch (error) {
      console.error('Failed to delete video:', error)
      message.error('Failed to delete video')
    }
  }

  const handlePreview = videoId => {
    const video = videos.find(video => video.id === videoId)
    setSelectedVideo(video)
    setPreviewModalVisible(true)
  }

  const columns = [
    {
      title: 'Video Story',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <Tooltip
            title={record.voiceover_text}
            overlayStyle={{ maxWidth: '70vw', whiteSpace: 'pre-wrap' }}
          >
            <strong>
              {(record.voiceover_text && record.voiceover_text.length > 50
                ? record.voiceover_text.slice(0, 50) + '...'
                : record.voiceover_text) || 'Untitled Video'}
            </strong>
          </Tooltip>
          <br />
          <small style={{ color: '#666' }}>
            Created:{' '}
            {new Date(record.createdAt || Date.now()).toLocaleDateString()}
          </small>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <div className="actions-container">
          <Tooltip title="Preview">
            <Button
              shape="circle"
              icon="eye"
              disabled={!record.video_url}
              onClick={() => handlePreview(record.id)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon="edit"
              onClick={() => onEditVideo(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this video?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button shape="circle" type="danger" icon="delete" />
            </Tooltip>
          </Popconfirm>
        </div>
      )
    }
  ]

  const handleTableChange = paginationInfo => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }))
  }

  const filteredVides = useMemo(
    () =>
      searchText
        ? videos.filter(video => {
            return (
              video.voiceover_text &&
              video.voiceover_text
                .toLowerCase()
                .includes(searchText.toLowerCase())
            )
          })
        : videos,
    [videos, searchText]
  )

  return (
    <div className="video-list">
      <div className="video-list__header">
        <div className="video-list__header-content">
          <div className="video-list__header-icon">
            <Icon type="video-camera" style={{ color: '#5057d5' }} />
          </div>
          <div className="video-list__header-info">
            <h2>AI Videos</h2>
            <p>Manage your AI-generated videos</p>
          </div>
        </div>
        <div className="video-list__header-actions">
          <NewHeader />
        </div>
      </div>
      <div className="video-list__filters">
        <div className="video-list__filters-search">
          <Search
            placeholder="Search videos..."
            style={{ width: 300 }}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>
        <div className="video-list__filters-actions">
          <Button type="primary" icon="plus" size="large" onClick={onCreateNew}>
            Create New Video
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredVides}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} videos`
        }}
        onChange={handleTableChange}
        rowKey="id"
        className="video-list__table"
      />
      {selectedVideo && selectedVideo.video_url && (
        <AIVideoModal
          visible={true}
          videoUrl={selectedVideo.video_url}
          title={'Your AI Generated Video'}
          onClose={() => setSelectedVideo({})}
        />
      )}
    </div>
  )
}

export default VideoList
